<script setup>
import { computed, onBeforeUnmount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import NextButton from 'dashboard/components-next/button/Button.vue';
import KaptionPhoneRequestIcon from './KaptionPhoneRequestIcon.vue';

const props = defineProps({
  channelType: { type: String, default: '' },
  provider: { type: String, default: '' },
  phoneNumber: { type: String, default: '' },
  hasBsuidAlias: { type: Boolean, default: false },
  outboundRecipientEnabled: { type: Boolean, default: false },
  canSendMessages: { type: Boolean, default: false },
  canReply: { type: Boolean, default: false },
  featureEnabled: { type: Boolean, default: false },
  onSend: { type: Function, required: true },
  cooldownMs: { type: Number, default: 30000 },
});

const { t } = useI18n();
const isSending = ref(false);
const isSent = ref(false);
let cooldownTimer;

// whatsapp-bsuid-coexistence.PHONE_REQUEST.1
const isVisible = computed(
  () =>
    props.featureEnabled &&
    props.channelType === 'Channel::Whatsapp' &&
    props.provider === 'whatsapp_cloud' &&
    !props.phoneNumber?.trim() &&
    props.hasBsuidAlias &&
    props.outboundRecipientEnabled &&
    props.canSendMessages
);

const isDisabled = computed(
  () => !props.canReply || isSending.value || isSent.value
);

const label = computed(() => {
  if (isSending.value) return t('CONVERSATION.REPLYBOX.PHONE_REQUEST.SENDING');
  if (isSent.value) return t('CONVERSATION.REPLYBOX.PHONE_REQUEST.SENT');
  return t('CONVERSATION.REPLYBOX.PHONE_REQUEST.LABEL');
});

const tooltip = computed(() => {
  if (!props.canReply) {
    return t('CONVERSATION.REPLYBOX.PHONE_REQUEST.OUTSIDE_WINDOW');
  }
  if (isSending.value) return t('CONVERSATION.REPLYBOX.PHONE_REQUEST.SENDING');
  if (isSent.value) return t('CONVERSATION.REPLYBOX.PHONE_REQUEST.SENT');
  return t('CONVERSATION.REPLYBOX.PHONE_REQUEST.TOOLTIP');
});

const requestMessage = computed(() =>
  t('CONVERSATION.REPLYBOX.PHONE_REQUEST.MESSAGE')
);

// whatsapp-bsuid-coexistence.PHONE_REQUEST.5
const requestPhoneNumber = async () => {
  if (!isVisible.value || isDisabled.value) return;

  isSending.value = true;
  try {
    await props.onSend(requestMessage.value);
    isSent.value = true;
    cooldownTimer = window.setTimeout(() => {
      isSent.value = false;
    }, props.cooldownMs);
  } catch {
    // The caller surfaces the API error; retaining availability permits retry.
  } finally {
    isSending.value = false;
  }
};

onBeforeUnmount(() => window.clearTimeout(cooldownTimer));
</script>

<template>
  <!-- NOTICE: KAPTION-INTEGRATION - Icon-only round sibling reply action. -->
  <NextButton
    v-if="isVisible"
    v-tooltip.top-end="tooltip"
    type="button"
    slate
    faded
    sm
    :disabled="isDisabled"
    :is-loading="isSending"
    :aria-label="label"
    :title="tooltip"
    data-testid="kaption-phone-request-action"
    @click="requestPhoneNumber"
  >
    <template #icon>
      <KaptionPhoneRequestIcon class="size-4" />
    </template>
  </NextButton>
  <template v-else />
</template>
